import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Plus,
  Calendar,
  Clock,
  FileText,
  X,
  ArrowLeft,
  User2,
  Briefcase,
  Building2,
  Search,
  Image as ImageIcon,
  Layers,
  Trash2,
  ChevronRight,
  Edit2,
} from "lucide-react";
import {
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Checkbox,
  Tabs,
  Tab,
} from "@mui/material";
import {
  LocalizationProvider,
  DateTimePicker,
  DatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

// Sample data for demonstration
const karigars = [
  { id: 1, name: "John Doe", specialization: "Cutting" },
  { id: 2, name: "Jane Smith", specialization: "Stitching" },
  { id: 3, name: "Mike Wilson", specialization: "Finishing" },
];

const jobTypes = [
  { id: 1, name: "Cutting" },
  { id: 2, name: "Stitching" },
  { id: 3, name: "Finishing" },
];

const agents = [
  { id: 1, name: "Direct" },
  { id: 2, name: "Agent 1" },
  { id: 3, name: "Agent 2" },
];

const units = ["meters", "pieces", "kg", "grams", "yards"];

const colors = ["Red", "Blue", "Green", "Black", "White"];

// Sample materials data
const dummyMaterials = [
  {
    id: 1,
    name: "Cotton Fabric",
    colors: "Red, Blue, Green",
    unit: "meters",
    avgPrice: 150,
    photo: null,
  },
  {
    id: 2,
    name: "Silk Fabric",
    colors: "Black, White, Gold",
    unit: "meters",
    avgPrice: 500,
    photo: null,
  },
  {
    id: 3,
    name: "Buttons",
    colors: "Silver, Gold, Black",
    unit: "pieces",
    avgPrice: 5,
    photo: null,
  },
  {
    id: 4,
    name: "Zippers",
    colors: "Black, White",
    unit: "pieces",
    avgPrice: 15,
    photo: null,
  },
  {
    id: 5,
    name: "Thread",
    colors: "All Colors",
    unit: "pieces",
    avgPrice: 25,
    photo: null,
  },
];

// Sample designs data
const dummyDesigns = [
  {
    id: 1,
    name: "Floral Pattern Dress",
    designId: "DSG001",
    image: null,
    createdAt: "2024-03-15",
    description: "A beautiful floral pattern for summer dresses",
    category: "Dresses",
    designer: "Jane Smith",
  },
  {
    id: 2,
    name: "Classic Suit Design",
    designId: "DSG002",
    image: null,
    createdAt: "2024-03-14",
    description: "Traditional suit design with modern elements",
    category: "Suits",
    designer: "John Doe",
  },
  {
    id: 3,
    name: "Ethnic Wear Collection",
    designId: "DSG003",
    image: null,
    createdAt: "2024-03-13",
    description: "Traditional ethnic wear with contemporary touch",
    category: "Ethnic",
    designer: "Priya Sharma",
  },
  {
    id: 4,
    name: "Summer Casual Wear",
    designId: "DSG004",
    image: null,
    createdAt: "2024-03-12",
    description: "Light and comfortable summer casual wear",
    category: "Casual",
    designer: "Mike Wilson",
  },
];

// Sample piece options
const pieceOptions = [
  { value: "Bottom", label: "Bottom" },
  { value: "Dupatta", label: "Dupatta" },
  { value: "Kurti", label: "Kurti" },
  { value: "Top", label: "Top" },
];

// Add size options
const sizeOptions = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];

const CreateJobslip = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const editId = params.get("id");

  // If we're in edit mode, fetch the job slip data
  useEffect(() => {
    if (editId) {
      // In a real app, this would be an API call to fetch the job slip data
      console.log("Fetching job slip data for ID:", editId);
    }
  }, [editId]);

  // Helper function to create a new piece structure
  const createNewPiece = (pieceType = "Bottom") => ({
    pieceType,
    jobRate: "",
    sizeRatio: {},
    colors: [],
  });

  // Helper function to create a new color structure
  const createNewColor = () => ({
    name: "",
    quantity: 1,
    unit: "meters",
    sizes: {},
    isManuallyOverridden: false,
  });

  const [form, setForm] = useState({
    generatedDateTime: dayjs(),
    jobslipNo: "",
    karigar: "",
    jobType: "",
    agent: "",
    expectedDate: dayjs(),
    notes: "",
  });

  // Add Material States
  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
  const [showDesignMaterialModal, setShowDesignMaterialModal] = useState(false);
  const [expandedMaterials, setExpandedMaterials] = useState({});
  const [designMaterialRows, setDesignMaterialRows] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [tempSelectedMaterials, setTempSelectedMaterials] = useState([]);
  const [materialRows, setMaterialRows] = useState({});
  const [showAddColorModal, setShowAddColorModal] = useState(false);
  const [newColor, setNewColor] = useState("");
  const [colorError, setColorError] = useState("");
  const [itemImages, setItemImages] = useState({});
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);
  const [itemNotes, setItemNotes] = useState({});
  const [currentNote, setCurrentNote] = useState("");
  const [isMaterialSectionCollapsed, setIsMaterialSectionCollapsed] =
    useState(true);
  const [isDesignSectionCollapsed, setIsDesignSectionCollapsed] =
    useState(true);
  const [designInformation, setDesignInformation] = useState(null);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAddJobTypeModal, setShowAddJobTypeModal] = useState(false);
  const [newJobType, setNewJobType] = useState("");
  const [jobTypeError, setJobTypeError] = useState("");

  // Add Taka related states
  const [showTakaModal, setShowTakaModal] = useState(false);
  const [selectedStockEntry, setSelectedStockEntry] = useState(null);
  const [takaDenominations, setTakaDenominations] = useState({});
  const [newTakaQuantity, setNewTakaQuantity] = useState("");
  const [newTakaValue, setNewTakaValue] = useState("");
  const [takaInputFields, setTakaInputFields] = useState({});
  const [lastSelectedUnit, setLastSelectedUnit] = useState("");

  // Add Sales Order related states
  const [isSalesOrderSectionCollapsed, setIsSalesOrderSectionCollapsed] =
    useState(true);
  const [linkedSalesOrder, setLinkedSalesOrder] = useState(null);

  // Add Design related states
  const [showDesignModal, setShowDesignModal] = useState(false);
  const [designSearchQuery, setDesignSearchQuery] = useState("");
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [tempSelectedDesigns, setTempSelectedDesigns] = useState([]);
  const [filteredDesigns, setFilteredDesigns] = useState(dummyDesigns);

  // Add size options
  const [showRatioModal, setShowRatioModal] = useState(false);
  const [showSizeDenominationModal, setShowSizeDenominationModal] =
    useState(false);
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [currentColorIndex, setCurrentColorIndex] = useState(null);
  const [tempRatios, setTempRatios] = useState({});
  const [tempSizeDenominations, setTempSizeDenominations] = useState({});
  const [currentPieceIndex, setCurrentPieceIndex] = useState(null);

  // Add color related states
  const [showColorModal, setShowColorModal] = useState(false);
  const [currentDesignForColor, setCurrentDesignForColor] = useState(null);
  const [tempColor, setTempColor] = useState("");
  const [tempQuantity, setTempQuantity] = useState("");
  const [tempUnit, setTempUnit] = useState("");
  const [tempSizes, setTempSizes] = useState({});

  // Add new state for size quantities modal
  const [showSizeQuantitiesModal, setShowSizeQuantitiesModal] = useState(false);
  const [tempSizeQuantities, setTempSizeQuantities] = useState({});

  const [designMaterials, setDesignMaterials] = useState({});

  const handleChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.jobslipNo) newErrors.jobslipNo = "Job slip number is required";
    if (!form.karigar) newErrors.karigar = "Karigar is required";
    if (!form.jobType) newErrors.jobType = "Job type is required";
    if (!form.agent) newErrors.agent = "Agent is required";
    if (!form.expectedDate)
      newErrors.expectedDate = "Expected date is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        console.log("Form submitted:", form);
        setIsSubmitting(false);
        setShowSuccessModal(true);
      }, 1000);
    }
  };

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  const handleCancelConfirmed = () => {
    setForm({
      generatedDateTime: dayjs(),
      jobslipNo: "",
      karigar: "",
      jobType: "",
      agent: "",
      expectedDate: dayjs(),
      notes: "",
    });
    setErrors({});
    setShowCancelDialog(false);
    navigate(-1);
  };

  const handleAddJobType = () => {
    setNewJobType("");
    setJobTypeError("");
    setShowAddJobTypeModal(true);
  };

  const handleJobTypeSubmit = () => {
    const trimmedJobType = newJobType.trim();
    if (!trimmedJobType) {
      setJobTypeError("Job type name is required");
      return;
    }
    if (
      jobTypes.some(
        type => type.name.toLowerCase() === trimmedJobType.toLowerCase(),
      )
    ) {
      setJobTypeError("This job type already exists");
      return;
    }
    // Add new job type (in real app, this would be an API call)
    jobTypes.push({ id: jobTypes.length + 1, name: trimmedJobType });
    setShowAddJobTypeModal(false);
    setNewJobType("");
  };

  // Add Material Handlers
  const handleAddMaterial = () => {
    setShowAddMaterialModal(true);
    setSearchQuery("");
    setTempSelectedMaterials([]);
  };

  const handleAddDesignMaterial = designId => {
    setCurrentDesignId(designId);
    setShowDesignMaterialModal(true);
  };

  const handleMaterialExpand = materialId => {
    setExpandedMaterials(prev => ({
      ...prev,
      [materialId]: !prev[materialId],
    }));
    // Initialize design material rows if not exists
    if (!designMaterialRows[materialId]) {
      setDesignMaterialRows(prev => ({
        ...prev,
        [materialId]: [{ color: "", quantity: "", pieces: "" }],
      }));
    }
  };

  const handleDesignRowChange = (materialId, index, field, value) => {
    setDesignMaterialRows(prev => ({
      ...prev,
      [materialId]: prev[materialId].map((row, i) =>
        i === index
          ? {
              ...row,
              [field]: value,
              // Auto-calculate pieces when quantity changes
              pieces:
                field === "quantity"
                  ? Math.floor(parseFloat(value) / 2).toString()
                  : row.pieces,
            }
          : row,
      ),
    }));
  };

  const filteredMaterials = dummyMaterials.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.colors.toLowerCase().includes(searchLower)
    );
  });

  const handleMaterialSelect = item => {
    setTempSelectedMaterials(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.filter(i => i.id !== item.id);
      } else {
        const processedItem = {
          id: item.id,
          name: item.name,
          colors: item.colors,
          unit: item.unit,
          price: item.avgPrice,
          photo: item.photo,
        };
        return [...prev, processedItem];
      }
    });
  };

  const handleAddSelectedMaterials = () => {
    const newItems = tempSelectedMaterials.filter(
      item => !selectedMaterials.find(i => i.id === item.id),
    );
    setSelectedMaterials(prev => [...prev, ...newItems]);
    setShowAddMaterialModal(false);
  };

  const handleRemoveItem = itemId => {
    setSelectedMaterials(selectedMaterials.filter(item => item.id !== itemId));
    const newMaterialRows = { ...materialRows };
    delete newMaterialRows[itemId];
    setMaterialRows(newMaterialRows);
  };

  const handleAddMaterialColorRow = materialId => {
    setMaterialRows(prev => ({
      ...prev,
      [materialId]: [
        ...(prev[materialId] || []),
        { color: "", quantity: 1, price: 0, unit: "meters" },
      ],
    }));
  };

  const handleRowChange = (materialId, index, field, value) => {
    setMaterialRows(prev => ({
      ...prev,
      [materialId]: prev[materialId].map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleRemoveRow = (materialId, index) => {
    setMaterialRows(prev => ({
      ...prev,
      [materialId]: prev[materialId].filter((_, i) => i !== index),
    }));
  };

  const handleAddColorClick = designId => {
    setCurrentDesignForColor(designId);
    setShowColorModal(true);
  };

  const handleColorSave = (designId, colorData) => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId) return design;
        const updatedPieces = [...design.pieces];
        updatedPieces[currentPieceIndex] = {
          ...updatedPieces[currentPieceIndex],
          colors: [...updatedPieces[currentPieceIndex].colors, colorData],
        };
        return { ...design, pieces: updatedPieces };
      }),
    );
    setShowColorModal(false);
  };

  const handleRemoveColor = (designId, pieceIndex, colorIndex) => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId) return design;
        const updatedPieces = [...design.pieces];
        const piece = updatedPieces[pieceIndex];
        piece.colors = piece.colors.filter((_, idx) => idx !== colorIndex);
        return { ...design, pieces: updatedPieces };
      }),
    );
  };

  const calculateSizeQuantities = (totalQuantity, ratios) => {
    if (!ratios || !totalQuantity) return {};

    // Get total ratio parts
    const totalRatioParts = Object.values(ratios).reduce(
      (sum, value) => sum + (parseInt(value) || 0),
      0,
    );

    if (totalRatioParts === 0) return {};

    // Calculate quantities for each size
    return Object.entries(ratios).reduce((acc, [size, ratio]) => {
      if (ratio && ratio !== "0") {
        acc[size] = Math.round(
          (parseInt(ratio) / totalRatioParts) * totalQuantity,
        );
      }
      return acc;
    }, {});
  };

  const handleColorChange = (
    designId,
    pieceIndex,
    colorIndex,
    field,
    value,
  ) => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId) return design;
        const updatedPieces = [...design.pieces];
        const piece = updatedPieces[pieceIndex];
        const updatedColors = [...piece.colors];
        const updatedColor = { ...updatedColors[colorIndex], [field]: value };

        // If quantity changed and ratios exist, recalculate sizes
        if (
          field === "quantity" &&
          piece.sizeRatio &&
          !updatedColor.isManuallyOverridden
        ) {
          updatedColor.sizes = calculateSizeQuantities(value, piece.sizeRatio);
        }

        updatedColors[colorIndex] = updatedColor;
        piece.colors = updatedColors;
        return { ...design, pieces: updatedPieces };
      }),
    );
  };

  const handleAddDesignColorRow = (designId, pieceIndex) => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId) return design;
        const updatedPieces = [...design.pieces];
        const piece = updatedPieces[pieceIndex];

        const newColor = createNewColor();
        if (piece.sizeRatio && Object.keys(piece.sizeRatio).length > 0) {
          newColor.sizes = calculateSizeQuantities(
            newColor.quantity,
            piece.sizeRatio,
          );
        }

        piece.colors.push(newColor);
        return { ...design, pieces: updatedPieces };
      }),
    );
  };

  const handleSizeOverride = (designId, colorIndex, newSizes) => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId) return design;
        const updatedPieces = [...design.pieces];
        const piece = updatedPieces[currentPieceIndex];
        const updatedColors = [...piece.colors];
        const updatedColor = {
          ...updatedColors[colorIndex],
          sizes: newSizes,
          isManuallyOverridden: true, // Mark as manually overridden
        };
        updatedColors[colorIndex] = updatedColor;
        piece.colors = updatedColors;
        return { ...design, pieces: updatedPieces };
      }),
    );
    setShowRatioModal(false);
  };

  const handleAddSizeDenomination = (designId, colorIndex) => {
    setCurrentDesignId(designId);
    setCurrentColorIndex(colorIndex);
    const currentSizes =
      selectedDesigns.find(d => d.id === designId)?.pieces[currentPieceIndex]
        ?.sizes || {};
    setTempSizeDenominations(currentSizes);
    setShowSizeDenominationModal(true);
  };

  const handleAddNote = itemId => {
    setCurrentItemId(itemId);
    setCurrentNote(itemNotes[itemId] || "");
    setShowNoteModal(true);
  };

  const handleSaveNote = () => {
    if (currentItemId) {
      setItemNotes(prev => ({
        ...prev,
        [currentItemId]: currentNote,
      }));
      setShowNoteModal(false);
    }
  };

  const handleImageUpload = (itemId, event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => URL.createObjectURL(file));

    setItemImages(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...newImages],
    }));
  };

  const handleRemoveImage = (itemId, imageIndex) => {
    setItemImages(prev => ({
      ...prev,
      [itemId]: prev[itemId].filter((_, index) => index !== imageIndex),
    }));
  };

  // Add Taka related handlers
  const handleAddTakaClick = stockEntry => {
    setSelectedStockEntry(stockEntry);
    setShowTakaModal(true);
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const handleAddTakaDenomination = () => {
    if (!newTakaQuantity || !newTakaValue) return;

    const quantity = parseFloat(newTakaQuantity);
    const value = parseFloat(newTakaValue);

    if (quantity <= 0 || value <= 0) return;

    const entryId = selectedStockEntry.id;
    const currentFields = takaInputFields[entryId] || [];

    setTakaInputFields(prev => ({
      ...prev,
      [entryId]: [
        ...currentFields,
        {
          quantity: newTakaQuantity,
          value: newTakaValue,
          id: Date.now(),
        },
      ],
    }));

    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const handleRemoveTakaField = (entryId, fieldId) => {
    setTakaInputFields(prev => ({
      ...prev,
      [entryId]: prev[entryId]?.filter(field => field.id !== fieldId) || [],
    }));
  };

  const handleCloseTakaModal = () => {
    setShowTakaModal(false);
    setSelectedStockEntry(null);
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const shouldShowTakaButton = unit => {
    return ["meters", "kg", "yards"].includes(unit);
  };

  // Add Design related handlers
  const handleDesignSelect = design => {
    setTempSelectedDesigns(prev => {
      const exists = prev.find(d => d.id === design.id);
      if (exists) {
        return prev.filter(d => d.id !== design.id);
      } else {
        return [...prev, design];
      }
    });
  };

  const handleAddSelectedDesigns = () => {
    setSelectedDesigns(
      tempSelectedDesigns.map(design => ({
        ...design,
        pieces: [createNewPiece()], // Initialize with one Bottom piece
      })),
    );
    setDesignInformation(true);
    setShowDesignModal(false);
  };

  const handlePieceChange = (designId, pieceIndex, newPieceType) => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId) return design;
        const updatedPieces = [...design.pieces];
        updatedPieces[pieceIndex] = {
          ...updatedPieces[pieceIndex],
          pieceType: newPieceType,
        };
        return { ...design, pieces: updatedPieces };
      }),
    );
  };

  const handleJobRateChange = (designId, pieceIndex, value) => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId) return design;
        const updatedPieces = [...design.pieces];
        updatedPieces[pieceIndex] = {
          ...updatedPieces[pieceIndex],
          jobRate: value,
        };
        return { ...design, pieces: updatedPieces };
      }),
    );
  };

  const handleAddPiece = designId => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId) return design;
        return {
          ...design,
          pieces: [...design.pieces, createNewPiece()],
        };
      }),
    );
  };

  const handleRemovePiece = (designId, pieceIndex) => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== designId) return design;
        const updatedPieces = [...design.pieces];
        updatedPieces.splice(pieceIndex, 1);
        return { ...design, pieces: updatedPieces };
      }),
    );
  };

  const handleRemoveDesign = designId => {
    setSelectedDesigns(prev => prev.filter(d => d.id !== designId));
    if (selectedDesigns.length === 1) {
      setDesignInformation(null);
    }
  };

  // Update filteredDesigns when search query changes
  useEffect(() => {
    const searchLower = designSearchQuery.toLowerCase();
    setFilteredDesigns(
      dummyDesigns.filter(
        design =>
          design.name.toLowerCase().includes(searchLower) ||
          design.designId.toLowerCase().includes(searchLower) ||
          design.category.toLowerCase().includes(searchLower),
      ),
    );
  }, [designSearchQuery]);

  // Add ratio handlers
  const handleRatioChange = (size, value) => {
    setTempRatios(prev => ({
      ...prev,
      [size]: value,
    }));
  };

  const handleSizeDenominationChange = (size, value) => {
    setTempSizeDenominations(prev => ({
      ...prev,
      [size]: value,
    }));
  };

  const handleAddRatioClick = (designId, pieceIndex) => {
    setCurrentDesignId(designId);
    setCurrentPieceIndex(pieceIndex);
    const currentRatios =
      selectedDesigns.find(d => d.id === designId)?.pieces[pieceIndex]
        ?.sizeRatio || {};
    setTempRatios(currentRatios);
    setShowRatioModal(true);
  };

  const handleSaveRatios = () => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== currentDesignId) return design;
        const updatedPieces = [...design.pieces];
        updatedPieces[currentPieceIndex] = {
          ...updatedPieces[currentPieceIndex],
          sizeRatio: tempRatios,
        };

        // Update all non-overridden color quantities based on new ratio
        const updatedColors = updatedPieces[currentPieceIndex].colors.map(
          color => {
            if (color.isManuallyOverridden) return color;
            return {
              ...color,
              sizes: calculateSizeQuantities(color.quantity, tempRatios),
            };
          },
        );

        updatedPieces[currentPieceIndex].colors = updatedColors;
        return { ...design, pieces: updatedPieces };
      }),
    );
    setShowRatioModal(false);
  };

  const handleSaveSizeDenominations = () => {
    setSelectedDesigns(prev =>
      prev.map(design => {
        if (design.id !== currentDesignId) return design;
        const updatedPieces = [...design.pieces];
        const piece = updatedPieces[currentPieceIndex];
        const updatedColors = [...piece.colors];
        updatedColors[currentColorIndex] = {
          ...updatedColors[currentColorIndex],
          sizes: tempSizeDenominations,
          isManuallyOverridden: true,
        };
        piece.colors = updatedColors;
        return { ...design, pieces: updatedPieces };
      }),
    );
    setShowSizeDenominationModal(false);
  };

  const handleSizeQuantityChange = (size, value) => {
    setTempSizeQuantities(prev => ({
      ...prev,
      [size]: value,
    }));
  };

  const handleSaveSizeQuantities = () => {
    if (currentColorIndex !== null && currentDesignForColor) {
      setSelectedDesigns(prev =>
        prev.map(design => {
          if (design.id !== currentDesignForColor) return design;
          const updatedPieces = [...design.pieces];
          const piece = updatedPieces[currentPieceIndex];
          const updatedColors = [...piece.colors];
          const updatedColor = {
            ...updatedColors[currentColorIndex],
            sizes: tempSizeQuantities,
            isManuallyOverridden: true,
          };
          updatedColors[currentColorIndex] = updatedColor;
          piece.colors = updatedColors;
          return { ...design, pieces: updatedPieces };
        }),
      );
    }
    setShowSizeQuantitiesModal(false);
  };

  const handleAddSelectedDesignMaterials = () => {
    // Get the current design's materials
    const updatedMaterials = {};

    selectedMaterials.forEach(material => {
      if (
        designMaterialRows[material.id] &&
        designMaterialRows[material.id].length > 0
      ) {
        updatedMaterials[material.id] = {
          materialInfo: material,
          rows: designMaterialRows[material.id].map(row => ({
            quantity: parseFloat(row.quantity) || 0,
            pieces: parseInt(row.pieces) || 0,
            avgPerPiece:
              row.pieces > 0
                ? (parseFloat(row.quantity) / parseInt(row.pieces)).toFixed(2)
                : 0,
          })),
        };
      }
    });

    setDesignMaterials(prev => ({
      ...prev,
      [currentDesignId]: updatedMaterials,
    }));

    setShowDesignMaterialModal(false);
  };

  const handleRemoveMaterialRow = (designId, materialId, rowIndex) => {
    setDesignMaterials(prev => {
      const designMaterials = prev[designId];
      const materialData = designMaterials[materialId];

      // Filter out the specific row
      const updatedRows = materialData.rows.filter(
        (_, idx) => idx !== rowIndex,
      );

      // If no rows left for this material, remove the material entirely
      if (updatedRows.length === 0) {
        const { [materialId]: _, ...restMaterials } = designMaterials;
        return {
          ...prev,
          [designId]: restMaterials,
        };
      }

      // Otherwise update the rows for this material
      return {
        ...prev,
        [designId]: {
          ...designMaterials,
          [materialId]: {
            ...materialData,
            rows: updatedRows,
          },
        },
      };
    });
  };

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <button
            onClick={handleCancel}
            className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            {editId ? `Edit Job Slip ${editId}` : "Create Job Slip"}
          </h1>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-base font-medium text-gray-800 mb-4">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Date and Time of Generating Slip"
                  value={form.generatedDateTime}
                  onChange={newValue =>
                    handleChange("generatedDateTime", newValue)
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      required: true,
                      error: Boolean(errors.generatedDateTime),
                      helperText: errors.generatedDateTime,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Clock size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                label="Lot No/Manual Jobslip No."
                size="small"
                required
                value={form.jobslipNo}
                onChange={e => handleChange("jobslipNo", e.target.value)}
                error={Boolean(errors.jobslipNo)}
                helperText={errors.jobslipNo}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileText size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl
                fullWidth
                size="small"
                error={Boolean(errors.karigar)}
                required>
                <InputLabel>Select Karigar</InputLabel>
                <Select
                  value={form.karigar}
                  label="Select Karigar"
                  onChange={e => handleChange("karigar", e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <User2 size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  }>
                  {karigars.map(karigar => (
                    <MenuItem key={karigar.id} value={karigar.id}>
                      {karigar.name} - {karigar.specialization}
                    </MenuItem>
                  ))}
                </Select>
                {errors.karigar && (
                  <span className="text-xs text-red-500 mt-1">
                    {errors.karigar}
                  </span>
                )}
              </FormControl>

              <FormControl
                fullWidth
                size="small"
                error={Boolean(errors.jobType)}
                required>
                <InputLabel>Select Job Type</InputLabel>
                <Select
                  value={form.jobType}
                  label="Select Job Type"
                  onChange={e => handleChange("jobType", e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <Briefcase size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  }>
                  {jobTypes.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                  <MenuItem
                    onClick={e => {
                      e.preventDefault();
                      handleAddJobType();
                    }}
                    sx={{
                      color: "#5652B0",
                      borderTop: "1px solid #e0e0e0",
                      marginTop: 1,
                      display: "flex",
                      gap: 1,
                    }}>
                    <Plus size={16} />
                    Add New Job Type
                  </MenuItem>
                </Select>
                {errors.jobType && (
                  <span className="text-xs text-red-500 mt-1">
                    {errors.jobType}
                  </span>
                )}
              </FormControl>

              <FormControl
                fullWidth
                size="small"
                error={Boolean(errors.agent)}
                required>
                <InputLabel>Select Agent</InputLabel>
                <Select
                  value={form.agent}
                  label="Select Agent"
                  onChange={e => handleChange("agent", e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <Building2 size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  }>
                  {agents.map(agent => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.agent && (
                  <span className="text-xs text-red-500 mt-1">
                    {errors.agent}
                  </span>
                )}
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expected Date"
                  value={form.expectedDate}
                  onChange={newValue => handleChange("expectedDate", newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      required: true,
                      error: Boolean(errors.expectedDate),
                      helperText: errors.expectedDate,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                label="Notes"
                multiline
                rows={1}
                fullWidth
                size="small"
                value={form.notes}
                onChange={e => handleChange("notes", e.target.value)}
                placeholder="Add any additional notes here..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileText size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
                className="md:col-span-3"
              />
            </div>
          </div>

          {/* Materials Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsMaterialSectionCollapsed(prev => !prev)}>
              <div className="flex items-center gap-1.5">
                <ChevronRight
                  size={16}
                  className={`text-gray-500 transition-transform ${
                    !isMaterialSectionCollapsed ? "rotate-90" : ""
                  }`}
                />
                <h2 className="text-base font-medium text-gray-800">
                  Materials
                </h2>
                {isMaterialSectionCollapsed && (
                  <span className="text-sm text-gray-500">
                    {selectedMaterials.length > 0 ? (
                      <>
                        :{" "}
                        {selectedMaterials.map(
                          (item, index) =>
                            item.name +
                            (index < selectedMaterials.length - 1 ? ", " : ""),
                        )}
                      </>
                    ) : (
                      ": No materials added yet"
                    )}
                  </span>
                )}
              </div>

              <button
                onClick={e => {
                  e.stopPropagation();
                  setIsMaterialSectionCollapsed(false);
                  handleAddMaterial();
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 w-36 rounded-lg text-sm font-medium text-[#5652B0] border border-[#5652B0] hover:bg-[#5652B0]/5 transition-all">
                <Plus size={14} />
                Add Material
              </button>
            </div>

            <div
              className={`space-y-4 ${
                isMaterialSectionCollapsed ? "hidden" : "mt-4"
              }`}>
              {selectedMaterials.length > 0 ? (
                <div className="space-y-4">
                  {selectedMaterials.map(item => (
                    <div
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex items-start gap-4">
                          <img
                            src={
                              item.photo ||
                              "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                            }
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg bg-gray-100"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start flex-col">
                                <h3 className="text-lg font-medium text-gray-800 ml-0">
                                  {item.name}
                                </h3>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {item.colors} • {item.unit}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleAddNote(item.id)}
                                  className="p-2 text-gray-500 hover:text-[#5652B0] hover:bg-[#5652B0]/5 rounded-lg transition-colors">
                                  <FileText size={18} />
                                </button>
                                <label
                                  className="p-2 text-gray-500 hover:text-[#5652B0] hover:bg-[#5652B0]/5 rounded-lg transition-colors cursor-pointer"
                                  htmlFor={`image-upload-${item.id}`}>
                                  <ImageIcon size={18} />
                                  <input
                                    type="file"
                                    id={`image-upload-${item.id}`}
                                    className="hidden"
                                    multiple
                                    accept="image/*"
                                    onChange={e =>
                                      handleImageUpload(item.id, e)
                                    }
                                  />
                                </label>
                                <button
                                  onClick={() => handleRemoveItem(item.id)}
                                  className="p-2 text-[#9B1C1C] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Note Display Section */}
                      {itemNotes[item.id] && (
                        <div className="px-4 py-3 border-t border-gray-100">
                          <div className="flex items-start gap-2">
                            <FileText
                              size={16}
                              className="text-gray-500 mt-0.5"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-700 mb-1">
                                Note:
                              </div>
                              <div className="text-sm text-gray-600">
                                {itemNotes[item.id]}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Image Preview Section */}
                      {itemImages[item.id] &&
                        itemImages[item.id].length > 0 && (
                          <div className="px-4 py-3 border-t border-gray-100">
                            <div className="flex flex-wrap gap-3">
                              {itemImages[item.id].map((image, index) => (
                                <div key={index} className="relative group">
                                  <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden">
                                    <img
                                      src={image}
                                      alt={`Preview ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleRemoveImage(item.id, index)
                                    }
                                    className="absolute -top-1.5 -right-1.5 bg-white shadow-sm border border-gray-200 text-[#9B1C1C] rounded-full p-1 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100">
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      <div className="p-4">
                        <div className="flex flex-col gap-2">
                          {(materialRows[item.id] || []).map((row, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-4">
                              <div className="min-w-[200px]">
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Color</InputLabel>
                                  <Select
                                    value={row.color}
                                    label="Color"
                                    onChange={e =>
                                      handleRowChange(
                                        item.id,
                                        index,
                                        "color",
                                        e.target.value,
                                      )
                                    }>
                                    {colors.map(color => (
                                      <MenuItem key={color} value={color}>
                                        {color}
                                      </MenuItem>
                                    ))}
                                    <MenuItem
                                      onClick={e => {
                                        e.preventDefault();
                                        handleAddColorClick(item.id);
                                      }}
                                      sx={{
                                        color: "#5652B0",
                                        borderTop: "1px solid #e0e0e0",
                                        marginTop: 1,
                                        display: "flex",
                                        gap: 1,
                                      }}>
                                      <Plus size={16} />
                                      Add New Color
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </div>

                              <div className="min-w-[120px]">
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Unit</InputLabel>
                                  <Select
                                    value={row.unit}
                                    label="Unit"
                                    onChange={e => {
                                      handleRowChange(
                                        item.id,
                                        index,
                                        "unit",
                                        e.target.value,
                                      );
                                      setLastSelectedUnit(e.target.value);
                                    }}>
                                    <MenuItem value="meters">Meters</MenuItem>
                                    <MenuItem value="yards">Yards</MenuItem>
                                    <MenuItem value="pieces">Pieces</MenuItem>
                                    <MenuItem value="kg">Kilograms</MenuItem>
                                  </Select>
                                </FormControl>
                              </div>

                              <div className="min-w-[120px]">
                                <TextField
                                  fullWidth
                                  type="number"
                                  size="small"
                                  label="Quantity"
                                  value={row.quantity}
                                  onChange={e =>
                                    handleRowChange(
                                      item.id,
                                      index,
                                      "quantity",
                                      parseInt(e.target.value),
                                    )
                                  }
                                />
                              </div>

                              <div className="min-w-[120px]">
                                <TextField
                                  fullWidth
                                  type="number"
                                  size="small"
                                  label="Price"
                                  value={row.price}
                                  onChange={e =>
                                    handleRowChange(
                                      item.id,
                                      index,
                                      "price",
                                      parseFloat(e.target.value),
                                    )
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        ₹
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </div>

                              {shouldShowTakaButton(row.unit) && (
                                <div className="min-w-[100px]">
                                  <button
                                    type="button"
                                    onClick={() => handleAddTakaClick(row)}
                                    className="w-full px-3 py-2 text-xs font-medium text-[#5652B0] border border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                                    {takaInputFields[row.id]?.length > 0
                                      ? "Edit Taka"
                                      : "Add Taka"}
                                  </button>
                                </div>
                              )}

                              <div className="ml-auto flex items-center gap-4">
                                <div className="text-right">
                                  <div className="text-sm text-gray-500">
                                    Subtotal
                                  </div>
                                  <div className="font-medium text-gray-800">
                                    ₹{row.quantity * row.price}
                                  </div>
                                </div>
                                <IconButton
                                  onClick={() =>
                                    handleRemoveRow(item.id, index)
                                  }
                                  className="mt-1"
                                  size="small">
                                  <Trash2
                                    size={18}
                                    className="text-[#9B1C1C] hover:text-red-600"
                                  />
                                </IconButton>
                              </div>
                            </div>
                          ))}

                          {/* Taka Display Inline */}
                          {takaInputFields[item.id]?.length > 0 && (
                            <div className="ml-4 flex items-center gap-2">
                              <span className="text-xs text-gray-600 font-medium">
                                Taka:
                              </span>
                              <div className="flex flex-wrap gap-1">
                                {takaInputFields[item.id].map(field => (
                                  <span
                                    key={field.id}
                                    className="px-2 py-1 bg-gray-100 text-xs font-medium rounded">
                                    {field.quantity}x{field.value}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex">
                            <button
                              onClick={() => handleAddMaterialColorRow(item.id)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                              <Plus size={16} />
                              Add Color
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end border-t pt-4 mt-4">
                    <div className="text-lg font-medium">
                      Total: ₹
                      {selectedMaterials.reduce(
                        (sum, item) =>
                          sum +
                          (materialRows[item.id] || []).reduce(
                            (rowSum, row) => rowSum + row.quantity * row.price,
                            0,
                          ),
                        0,
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No materials added yet
                </div>
              )}
            </div>
          </div>

          {/* Design Information Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsDesignSectionCollapsed(prev => !prev)}>
              <div className="flex items-center gap-1.5">
                <ChevronRight
                  size={16}
                  className={`text-gray-500 transition-transform ${
                    !isDesignSectionCollapsed ? "rotate-90" : ""
                  }`}
                />
                <h2 className="text-base font-medium text-gray-800">
                  Design Information
                </h2>
                {isDesignSectionCollapsed && (
                  <span className="text-sm text-gray-500">
                    {designInformation
                      ? `: Design #123`
                      : ": No design information added yet"}
                  </span>
                )}
              </div>

              <button
                onClick={e => {
                  e.stopPropagation();
                  if (isDesignSectionCollapsed) {
                    setIsDesignSectionCollapsed(false);
                  }
                  setShowDesignModal(true);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 w-36 rounded-lg text-sm font-medium text-[#5652B0] border border-[#5652B0] hover:bg-[#5652B0]/5 transition-all">
                <Plus size={14} />
                Add Design
              </button>
            </div>

            <div
              className={`space-y-4 ${
                isDesignSectionCollapsed ? "hidden" : "mt-4"
              }`}>
              {designInformation ? (
                <div className="space-y-4">
                  {selectedDesigns.map(design => (
                    <div
                      key={design.id}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      {(design.pieces || []).map((piece, pieceIndex) => (
                        <React.Fragment key={pieceIndex}>
                          <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                                {design.image ? (
                                  <img
                                    src={design.image}
                                    alt={design.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <ImageIcon size={24} />
                                  </div>
                                )}
                              </div>

                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-start flex-col">
                                    <h3 className="text-base font-medium text-gray-800">
                                      {design.name}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                      Design ID: {design.designId}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleAddNote(design.id)}
                                      className="p-2 text-gray-500 hover:text-[#5652B0] hover:bg-[#5652B0]/5 rounded-lg transition-colors">
                                      <FileText size={18} />
                                    </button>

                                    <label
                                      className="p-2 text-gray-500 hover:text-[#5652B0] hover:bg-[#5652B0]/5 rounded-lg transition-colors cursor-pointer"
                                      htmlFor={`design-image-upload-${design.id}-${pieceIndex}`}>
                                      <ImageIcon size={18} />
                                      <input
                                        type="file"
                                        id={`design-image-upload-${design.id}-${pieceIndex}`}
                                        className="hidden"
                                        multiple
                                        accept="image/*"
                                        onChange={e =>
                                          handleImageUpload(design.id, e)
                                        }
                                      />
                                    </label>

                                    <button
                                      onClick={() =>
                                        handleAddDesignMaterial(design.id)
                                      }
                                      className="flex items-center justify-center gap-1.5 px-3 h-[40px] w-36 rounded-lg text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition-all">
                                      <Plus size={14} />
                                      Add Material
                                    </button>

                                    <FormControl
                                      size="small"
                                      sx={{ minWidth: 120 }}>
                                      <InputLabel>Select Piece</InputLabel>
                                      <Select
                                        value={piece.pieceType}
                                        label="Select Piece"
                                        onChange={e =>
                                          handlePieceChange(
                                            design.id,
                                            pieceIndex,
                                            e.target.value,
                                          )
                                        }>
                                        {pieceOptions.map(option => (
                                          <MenuItem
                                            key={option.value}
                                            value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>

                                    {!piece.sizeRatio ||
                                    !Object.values(piece.sizeRatio).some(
                                      value => value && value !== "0",
                                    ) ? (
                                      <button
                                        onClick={() =>
                                          handleAddRatioClick(
                                            design.id,
                                            pieceIndex,
                                          )
                                        }
                                        className="flex items-center justify-center gap-1.5 px-3 h-[40px] w-36 rounded-lg text-sm font-medium text-[#5652B0] bg-[#5652B0]/5 hover:bg-[#5652B0]/10 transition-all">
                                        <Plus size={14} />
                                        Add Ratio
                                      </button>
                                    ) : (
                                      <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-[#5652B0] rounded-lg">
                                        <div className="flex items-center gap-1.5">
                                          {Object.entries(piece.sizeRatio)
                                            .filter(
                                              ([_, value]) =>
                                                value && value !== "0",
                                            )
                                            .map(
                                              ([size, value], index, array) => (
                                                <React.Fragment key={size}>
                                                  <span className="text-sm text-gray-700">
                                                    {size}:{" "}
                                                    <span className="font-medium">
                                                      {value}
                                                    </span>
                                                  </span>
                                                  {index < array.length - 1 && (
                                                    <span className="text-gray-400">
                                                      •
                                                    </span>
                                                  )}
                                                </React.Fragment>
                                              ),
                                            )}
                                        </div>
                                        <button
                                          onClick={() =>
                                            handleAddRatioClick(
                                              design.id,
                                              pieceIndex,
                                            )
                                          }
                                          className="ml-2 p-1 text-gray-500 hover:text-[#5652B0] rounded-full hover:bg-white transition-colors">
                                          <Edit2 size={14} />
                                        </button>
                                      </div>
                                    )}

                                    <TextField
                                      size="small"
                                      label="Job Rate"
                                      type="text"
                                      inputProps={{
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                      }}
                                      value={piece.jobRate}
                                      onChange={e =>
                                        handleJobRateChange(
                                          design.id,
                                          pieceIndex,
                                          e.target.value,
                                        )
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            ₹
                                          </InputAdornment>
                                        ),
                                      }}
                                      sx={{ width: "140px" }}
                                    />

                                    {pieceIndex > 0 && (
                                      <IconButton
                                        onClick={() =>
                                          handleRemovePiece(
                                            design.id,
                                            pieceIndex,
                                          )
                                        }
                                        size="small"
                                        className="text-[#9B1C1C]">
                                        <Trash2
                                          size={18}
                                          className="text-[#9B1C1C]"
                                        />
                                      </IconButton>
                                    )}

                                    {pieceIndex === 0 && (
                                      <IconButton
                                        onClick={() =>
                                          handleRemoveDesign(design.id)
                                        }
                                        size="small">
                                        <Trash2
                                          size={18}
                                          className="text-[#9B1C1C] hover:text-red-600 hover:bg-red-50"
                                        />
                                      </IconButton>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Note Display Section */}
                            {itemNotes[design.id] && (
                              <div className="px-4 py-3 border-t border-gray-100">
                                <div className="flex items-start gap-2">
                                  <FileText
                                    size={16}
                                    className="text-gray-500 mt-0.5"
                                  />
                                  <div>
                                    <div className="text-sm font-medium text-gray-700 mb-1">
                                      Note:
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {itemNotes[design.id]}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Image Preview Section */}
                            {itemImages[design.id] &&
                              itemImages[design.id].length > 0 && (
                                <div className="px-4 py-3 border-t border-gray-100">
                                  <div className="flex flex-wrap gap-3">
                                    {itemImages[design.id].map(
                                      (image, index) => (
                                        <div
                                          key={index}
                                          className="relative group">
                                          <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden">
                                            <img
                                              src={image}
                                              alt={`Preview ${index + 1}`}
                                              className="w-full h-full object-cover"
                                            />
                                          </div>
                                          <button
                                            onClick={() =>
                                              handleRemoveImage(
                                                design.id,
                                                index,
                                              )
                                            }
                                            className="absolute -top-1.5 -right-1.5 bg-white shadow-sm border border-gray-200 text-[#9B1C1C] rounded-full p-1 hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100">
                                            <X size={12} />
                                          </button>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}

                            <div className="p-4">
                              <div className="flex flex-col gap-2">
                                {/* Color rows section */}
                                {(design.pieces[pieceIndex].colors || []).map(
                                  (color, colorIndex) => (
                                    <div
                                      key={colorIndex}
                                      className="flex items-center gap-4">
                                      <div className="min-w-[200px]">
                                        <FormControl size="small" fullWidth>
                                          <InputLabel>Color</InputLabel>
                                          <Select
                                            value={color.name}
                                            label="Color"
                                            onChange={e =>
                                              handleColorChange(
                                                design.id,
                                                pieceIndex,
                                                colorIndex,
                                                "name",
                                                e.target.value,
                                              )
                                            }>
                                            {colors.map(color => (
                                              <MenuItem
                                                key={color}
                                                value={color}>
                                                {color}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </div>

                                      <div className="min-w-[120px]">
                                        <TextField
                                          fullWidth
                                          type="number"
                                          size="small"
                                          label="Quantity"
                                          value={color.quantity}
                                          onChange={e =>
                                            handleColorChange(
                                              design.id,
                                              pieceIndex,
                                              colorIndex,
                                              "quantity",
                                              e.target.value,
                                            )
                                          }
                                        />
                                      </div>

                                      <div className="min-w-[250px]">
                                        {Object.values(color.sizes || {}).some(
                                          v => v && v !== "0",
                                        ) ? (
                                          <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-[#5652B0] rounded-lg">
                                            <div className="flex-1 flex items-center gap-1.5">
                                              {Object.entries(color.sizes).map(
                                                ([size, qty], idx, arr) => (
                                                  <React.Fragment key={size}>
                                                    <span className="text-sm text-gray-700">
                                                      {size}:{" "}
                                                      <span className="font-medium">
                                                        {qty}
                                                      </span>
                                                    </span>
                                                    {idx < arr.length - 1 && (
                                                      <span className="text-gray-400">
                                                        •
                                                      </span>
                                                    )}
                                                  </React.Fragment>
                                                ),
                                              )}
                                            </div>
                                            <button
                                              onClick={() =>
                                                handleAddSizeDenomination(
                                                  design.id,
                                                  colorIndex,
                                                )
                                              }
                                              className="p-1 text-gray-500 hover:text-[#5652B0] rounded-full hover:bg-white transition-colors">
                                              <Edit2 size={14} />
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleAddSizeDenomination(
                                                design.id,
                                                colorIndex,
                                              )
                                            }
                                            className="w-full px-3 py-2 text-xs font-medium text-[#5652B0] border border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                                            Add Sizes Denomination
                                          </button>
                                        )}
                                      </div>

                                      <div className="min-w-[120px]">
                                        <FormControl size="small" fullWidth>
                                          <InputLabel>Unit</InputLabel>
                                          <Select
                                            value={color.unit}
                                            label="Unit"
                                            onChange={e =>
                                              handleColorChange(
                                                design.id,
                                                pieceIndex,
                                                colorIndex,
                                                "unit",
                                                e.target.value,
                                              )
                                            }>
                                            <MenuItem value="meters">
                                              Meters
                                            </MenuItem>
                                            <MenuItem value="yards">
                                              Yards
                                            </MenuItem>
                                            <MenuItem value="pieces">
                                              Pieces
                                            </MenuItem>
                                          </Select>
                                        </FormControl>
                                      </div>

                                      <IconButton
                                        onClick={() =>
                                          handleRemoveColor(
                                            design.id,
                                            pieceIndex,
                                            colorIndex,
                                          )
                                        }
                                        size="small">
                                        <Trash2
                                          size={18}
                                          className="text-[#9B1C1C] hover:text-red-600 hover:bg-red-50"
                                        />
                                      </IconButton>
                                    </div>
                                  ),
                                )}

                                <div className="flex items-start">
                                  <button
                                    onClick={() =>
                                      handleAddDesignColorRow(
                                        design.id,
                                        pieceIndex,
                                      )
                                    }
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                                    <Plus size={16} />
                                    Add Color
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Materials Table */}
                            {designMaterials[design.id] &&
                              Object.keys(designMaterials[design.id]).length >
                                0 && (
                                <div className="mt-4 border-t border-gray-200 pt-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-700">
                                      Materials Used
                                    </h4>
                                    <button
                                      onClick={() => {
                                        setCurrentDesignId(design.id);
                                        setShowDesignMaterialModal(true);
                                      }}
                                      className="text-xs text-[#5652B0] hover:text-[#4A47A3] font-medium">
                                      Edit Materials
                                    </button>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th
                                            scope="col"
                                            className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                            Material
                                          </th>
                                          <th
                                            scope="col"
                                            className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                            Total Material
                                          </th>
                                          <th
                                            scope="col"
                                            className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                            Pieces
                                          </th>
                                          <th
                                            scope="col"
                                            className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                            Avg/Piece
                                          </th>
                                          <th
                                            scope="col"
                                            className="px-3 py-2 text-left text-xs font-medium text-gray-500"></th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {Object.entries(
                                          designMaterials[design.id],
                                        ).map(([materialId, materialData]) =>
                                          materialData.rows.map(
                                            (row, rowIndex) => (
                                              <tr
                                                key={`${materialId}-${rowIndex}`}>
                                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 text-left">
                                                  {
                                                    materialData.materialInfo
                                                      .name
                                                  }
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 text-left">
                                                  {row.quantity}{" "}
                                                  {
                                                    materialData.materialInfo
                                                      .unit
                                                  }
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 text-left">
                                                  {row.pieces}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 text-left">
                                                  {row.avgPerPiece}{" "}
                                                  {
                                                    materialData.materialInfo
                                                      .unit
                                                  }
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-sm text-[#9B1C1C] text-left">
                                                  <IconButton
                                                    onClick={() =>
                                                      handleRemoveMaterialRow(
                                                        design.id,
                                                        materialId,
                                                        rowIndex,
                                                      )
                                                    }
                                                    size="small">
                                                    <Trash2
                                                      size={18}
                                                      className="text-[#9B1C1C] hover:text-red-600 hover:bg-red-50"
                                                    />
                                                  </IconButton>
                                                </td>
                                              </tr>
                                            ),
                                          ),
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                            <div className="flex w-full justify-end p-4 border-t">
                              <button
                                onClick={() => handleAddPiece(design.id)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#5652B0] border border-[#5652B0] hover:bg-[#5652B0]/5 transition-all">
                                <Plus size={16} />
                                Add Another Piece
                              </button>
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No design information added yet
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-4 mt-4 pb-8">
            <button
              onClick={handleCancel}
              className="flex items-center gap-2 px-5 py-2.5 text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors">
              <X size={18} />
              <span className="font-medium">Cancel</span>
            </button>

            <button
              onClick={handleSave}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-2.5 text-white bg-[#5652B0] hover:bg-[#4A47A3] rounded-lg transition-colors">
              {isSubmitting ? (
                <>
                  <CircularProgress size={18} color="inherit" />
                  <span className="font-medium">Saving...</span>
                </>
              ) : (
                <>
                  <Plus size={18} />
                  <span className="font-medium">Save Job Slip</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Material Selection Modal */}
      <Dialog
        open={showAddMaterialModal}
        onClose={() => setShowAddMaterialModal(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>
          <div className="flex items-center justify-between">
            <span>Select Materials</span>
            <IconButton
              onClick={() => setShowAddMaterialModal(false)}
              size="small">
              <X size={18} />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            <TextField
              fullWidth
              size="small"
              placeholder="Search materials..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} className="text-gray-500" />
                  </InputAdornment>
                ),
              }}
            />

            <div className="mt-4 space-y-2">
              {filteredMaterials.map(item => (
                <div
                  key={item.id}
                  onClick={() => handleMaterialSelect(item)}
                  className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer transition-all ${
                    tempSelectedMaterials.find(i => i.id === item.id)
                      ? "border-[#5652B0] bg-[#5652B0]/5"
                      : "border-gray-200 hover:border-[#5652B0] hover:bg-[#5652B0]/5"
                  }`}>
                  <Checkbox
                    checked={Boolean(
                      tempSelectedMaterials.find(i => i.id === item.id),
                    )}
                    onChange={() => handleMaterialSelect(item)}
                  />
                  <img
                    src={
                      item.photo ||
                      "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                    }
                    alt={item.name}
                    className="w-12 h-12 object-cover rounded-lg bg-gray-100"
                  />
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-800">
                      {item.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {item.colors} • {item.unit}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-800">
                      ₹{item.avgPrice}
                    </div>
                    <div className="text-xs text-gray-500">per {item.unit}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddMaterialModal(false)}>Cancel</Button>
          <Button
            onClick={handleAddSelectedMaterials}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": { backgroundColor: "#4A47A3" },
            }}>
            Add Selected ({tempSelectedMaterials.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog
        open={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note"
            fullWidth
            multiline
            rows={4}
            value={currentNote}
            onChange={e => setCurrentNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNoteModal(false)}>Cancel</Button>
          <Button
            onClick={handleSaveNote}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": { backgroundColor: "#4A47A3" },
            }}>
            Save Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Taka Modal */}
      <Dialog
        open={showTakaModal}
        onClose={handleCloseTakaModal}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Add Taka Details</DialogTitle>
        <DialogContent>
          <div className="py-4 space-y-4">
            <div className="flex items-center gap-4">
              <TextField
                label="Quantity"
                type="number"
                size="small"
                value={newTakaQuantity}
                onChange={e => setNewTakaQuantity(e.target.value)}
              />
              <TextField
                label="Value"
                type="number"
                size="small"
                value={newTakaValue}
                onChange={e => setNewTakaValue(e.target.value)}
              />
              <Button
                onClick={handleAddTakaDenomination}
                variant="contained"
                sx={{
                  backgroundColor: "#5652B0",
                  "&:hover": { backgroundColor: "#4A47A3" },
                }}>
                Add
              </Button>
            </div>

            {selectedStockEntry && (
              <div className="space-y-2">
                {takaInputFields[selectedStockEntry.id]?.map(field => (
                  <div
                    key={field.id}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span>
                      {field.quantity} x {field.value}
                    </span>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleRemoveTakaField(selectedStockEntry.id, field.id)
                      }>
                      <X size={16} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTakaModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Cancel Job Slip</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel? All unsaved changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            No, Keep Editing
          </Button>
          <Button
            onClick={handleCancelConfirmed}
            sx={{ color: "red" }}
            autoFocus>
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate(-1);
        }}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Job slip has been {editId ? "updated" : "created"} successfully.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSuccessModal(false);
              navigate(-1);
            }}
            autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Job Type Modal */}
      <Dialog
        open={showAddJobTypeModal}
        onClose={() => setShowAddJobTypeModal(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Add New Job Type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Job Type Name"
            fullWidth
            value={newJobType}
            onChange={e => setNewJobType(e.target.value)}
            error={Boolean(jobTypeError)}
            helperText={jobTypeError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddJobTypeModal(false)}>Cancel</Button>
          <Button
            onClick={handleJobTypeSubmit}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": { backgroundColor: "#4A47A3" },
            }}>
            Add Job Type
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Design Modal */}
      <Dialog
        open={showDesignModal}
        onClose={() => setShowDesignModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "0.5rem",
          },
        }}>
        <DialogTitle className="flex justify-between items-center">
          <span className="text-lg font-medium">Select Designs</span>
          <IconButton
            onClick={() => setShowDesignModal(false)}
            size="small"
            className="text-gray-500">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            <TextField
              fullWidth
              size="small"
              placeholder="Search designs..."
              value={designSearchQuery}
              onChange={e => setDesignSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} className="text-gray-500" />
                  </InputAdornment>
                ),
              }}
            />

            <div className="mt-4 space-y-2 max-h-[400px] overflow-y-auto">
              {filteredDesigns.map(design => (
                <div
                  key={design.id}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    tempSelectedDesigns.some(d => d.id === design.id)
                      ? "border-[#5652B0] bg-purple-50"
                      : "border-gray-200 hover:border-[#5652B0]"
                  }`}
                  onClick={() => handleDesignSelect(design)}>
                  <Checkbox
                    checked={tempSelectedDesigns.some(d => d.id === design.id)}
                    sx={{
                      color: "#5652B0",
                      "&.Mui-checked": {
                        color: "#5652B0",
                      },
                    }}
                  />
                  <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0 mr-3">
                    {design.image ? (
                      <img
                        src={design.image}
                        alt={design.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <ImageIcon size={24} />
                      </div>
                    )}
                  </div>
                  <div className="ml-2 flex-grow">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-base">{design.name}</p>
                        <p className="text-sm text-gray-500">
                          Design ID: {design.designId}
                        </p>
                      </div>
                      <p className="text-sm font-medium">
                        ₹{design.price || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDesignModal(false)}
            sx={{
              color: "gray",
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedDesigns}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            Add Selected ({tempSelectedDesigns.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ratio Modal */}
      <Dialog
        open={showRatioModal}
        onClose={() => setShowRatioModal(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle className="flex justify-between items-center">
          <span className="text-lg font-medium">Set Size Ratio</span>
          <IconButton
            onClick={() => setShowRatioModal(false)}
            size="small"
            className="text-gray-500">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            <div className="space-y-3">
              {sizeOptions.map(size => (
                <div key={size} className="flex items-center gap-4">
                  <div className="w-12 text-sm font-medium text-gray-700">
                    {size}
                  </div>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="0"
                    value={tempRatios[size] || ""}
                    onChange={e => handleRatioChange(size, e.target.value)}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        style: { textAlign: "center" },
                      },
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRatioModal(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRatios}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            Save Ratio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Size Quantities Modal */}
      <Dialog
        open={showSizeQuantitiesModal}
        onClose={() => setShowSizeQuantitiesModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "0.5rem",
          },
        }}>
        <DialogTitle className="flex justify-between items-center">
          <span className="text-lg font-medium">Edit Size Quantities</span>
          <IconButton
            onClick={() => setShowSizeQuantitiesModal(false)}
            size="small"
            className="text-gray-500">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            <div className="space-y-3">
              {Object.entries(tempSizeQuantities).map(([size, quantity]) => (
                <div key={size} className="flex items-center gap-4">
                  <div className="w-12 text-sm font-medium text-gray-700">
                    {size}
                  </div>
                  <TextField
                    size="small"
                    type="number"
                    value={quantity}
                    onChange={e =>
                      handleSizeQuantityChange(size, e.target.value)
                    }
                    InputProps={{
                      inputProps: {
                        min: 0,
                        style: { textAlign: "right" },
                      },
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowSizeQuantitiesModal(false)}
            sx={{
              color: "gray",
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSizeQuantities}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            Save Quantities
          </Button>
        </DialogActions>
      </Dialog>

      {/* Size Denomination Modal */}
      <Dialog
        open={showSizeDenominationModal}
        onClose={() => setShowSizeDenominationModal(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle className="flex justify-between items-center">
          <span className="text-lg font-medium">Set Size Quantities</span>
          <IconButton
            onClick={() => setShowSizeDenominationModal(false)}
            size="small"
            className="text-gray-500">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            <div className="space-y-3">
              {sizeOptions.map(size => (
                <div key={size} className="flex items-center gap-4">
                  <div className="w-12 text-sm font-medium text-gray-700">
                    {size}
                  </div>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="0"
                    value={tempSizeDenominations[size] || ""}
                    onChange={e =>
                      handleSizeDenominationChange(size, e.target.value)
                    }
                    InputProps={{
                      inputProps: {
                        min: 0,
                        style: { textAlign: "right" },
                      },
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSizeDenominationModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSizeDenominations}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            Save Quantities
          </Button>
        </DialogActions>
      </Dialog>

      {/* Design Material Modal */}
      <Dialog
        open={showDesignMaterialModal}
        onClose={() => setShowDesignMaterialModal(false)}
        maxWidth="lg"
        fullWidth>
        <DialogTitle>
          <div className="flex items-center justify-between">
            <span className="text-xl font-semibold">
              Add Material to Design
            </span>
            <IconButton
              onClick={() => setShowDesignMaterialModal(false)}
              size="small">
              <X size={18} />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            <div className="space-y-4">
              {selectedMaterials.map(material => (
                <div
                  key={material.id}
                  className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50/50">
                    <div className="flex items-center gap-4">
                      <img
                        src={
                          material.photo ||
                          "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?q=80&w=1935&auto=format&fit=crop"
                        }
                        alt={material.name}
                        className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                      />
                      <div>
                        <div className="flex items-center gap-3 mb-0.5">
                          <h3 className="text-base font-medium text-gray-800">
                            {material.name}
                          </h3>
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center px-2 py-0.5 text-xs border border-gray-200 rounded text-gray-600">
                              {material.colors}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 text-xs border border-gray-200 rounded text-gray-600">
                              {material.unit}
                            </span>
                          </div>
                        </div>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              sx={{
                                padding: "4px",
                                color: "#5652B0",
                                "&.Mui-checked": {
                                  color: "#5652B0",
                                },
                              }}
                            />
                          }
                          label={
                            <span className="text-sm text-gray-600">
                              Primary Material
                            </span>
                          }
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => handleMaterialExpand(material.id)}
                      className="p-1.5 text-gray-500 hover:bg-gray-100 transition-all">
                      <ChevronRight
                        size={18}
                        className={`transition-transform duration-200 ${
                          expandedMaterials[material.id] ? "rotate-90" : ""
                        }`}
                      />
                    </button>
                  </div>
                  <div
                    className={`px-4 pb-3 ${
                      !expandedMaterials[material.id] ? "hidden" : ""
                    }`}>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 border-b border-gray-200">
                        <div className="grid grid-cols-3 gap-6">
                          <div>
                            <div className="text-xs font-medium text-gray-500 mb-2">
                              Total Material
                            </div>
                            <div className="text-base font-semibold text-gray-800">
                              450{" "}
                              <span className="text-sm text-gray-500">
                                meters
                              </span>
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-gray-500 mb-2">
                              Meter per piece
                            </div>
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Enter value"
                              InputProps={{
                                inputProps: { min: 0, step: 0.1 },
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    borderColor: "#E5E7EB",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#5652B0",
                                  },
                                },
                              }}
                            />
                          </div>
                          <div>
                            <div className="text-xs font-medium text-gray-500 mb-2">
                              Piece Output
                            </div>
                            <div className="text-base font-semibold text-gray-800">
                              225{" "}
                              <span className="text-sm text-gray-500">
                                pieces
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-4 px-3 py-2 bg-gray-50">
                        <div className="text-xs font-medium text-gray-600">
                          Color
                        </div>
                        <div className="text-xs font-medium text-gray-600">
                          Total Material
                        </div>
                        <div className="text-xs font-medium text-gray-600">
                          Pieces Output
                        </div>
                        <div></div>
                      </div>
                      {(designMaterialRows[material.id] || []).map(
                        (row, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-4 gap-4 px-3 py-2 border-t border-gray-200">
                            <div className="text-sm text-gray-700">
                              {row.color || "Default"}
                            </div>
                            <div>
                              <TextField
                                size="small"
                                type="number"
                                value={row.quantity}
                                onChange={e =>
                                  handleDesignRowChange(
                                    material.id,
                                    index,
                                    "quantity",
                                    e.target.value,
                                  )
                                }
                                InputProps={{
                                  inputProps: { min: 0, step: 0.1 },
                                  endAdornment: (
                                    <span className="text-xs text-gray-500">
                                      {material.unit}
                                    </span>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#E5E7EB",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#5652B0",
                                    },
                                  },
                                }}
                              />
                            </div>
                            <div>
                              <TextField
                                size="small"
                                type="number"
                                value={row.pieces}
                                onChange={e =>
                                  handleDesignRowChange(
                                    material.id,
                                    index,
                                    "pieces",
                                    e.target.value,
                                  )
                                }
                                InputProps={{
                                  inputProps: { min: 0, step: 1 },
                                  endAdornment: (
                                    <span className="text-xs text-gray-500">
                                      pieces
                                    </span>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "#E5E7EB",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#5652B0",
                                    },
                                  },
                                }}
                              />
                            </div>
                            <div className="flex justify-end">
                              <button className="p-0.5 text-gray-400 hover:text-red-600 transition-colors">
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDesignMaterialModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedDesignMaterials}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": { backgroundColor: "#4A47A3" },
            }}>
            Add Selected Materials
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateJobslip;
